import React from 'react';
import type { Decision, IdeaFit, IdeaKind, SetupApp } from '../../services/setupApi';

/*
 * Shared pieces of the setup: the frame with its step line and progress, the
 * buttons, the Allow / Ask first / Block control, and the small chips. The
 * look follows the sign-in pages: warm canvas, ink buttons, hairlines.
 */

export const INPUT = 'h-[42px] w-full rounded-[8px] border border-[#dedede] bg-white px-3 text-[14px] text-[#18191d] outline-none placeholder:text-[#92949d] focus:border-[#18191d]';
export const FIELD_LABEL = 'mb-1.5 block text-[12.5px] font-semibold text-[#18191d]';
export const BTN = 'inline-flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-[#18191d] px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50';
export const BTN_OUTLINE = 'inline-flex h-[40px] items-center justify-center gap-2 rounded-[8px] border border-[#dedede] bg-white px-4 text-[13.5px] font-semibold text-[#18191d] transition-colors hover:border-[#18191d] disabled:opacity-50';
export const BTN_TEXT = 'inline-flex h-[40px] items-center text-[13px] font-medium text-[#666971] hover:text-[#18191d] disabled:opacity-50';
export const PANEL = 'rounded-[12px] border border-[#e6e6e3] bg-white p-5';

export const STEP_NAMES: Record<string, [string, string]> = {
  about: ['About you', 'A little context about your role'],
  apps: ['Your apps', 'Choose what you use'],
  permissions: ['Permissions', 'What AI may do in each app'],
  ideas: ['Ideas', 'See what could help'],
  connect: ['Connect', 'Only what your ideas need'],
  start: ['Start', 'Start useful work'],
};
const COUNTED = ['about', 'apps', 'permissions', 'ideas', 'connect', 'start'];

export const SetupFrame: React.FC<{ step: string; children: React.ReactNode; wide?: boolean }> = ({ step, children, wide }) => {
  const index = COUNTED.indexOf(step);
  const names = STEP_NAMES[step];
  let where: React.ReactNode = <span className="font-mono text-[11px] tracking-[0.1em] text-[#777982]">FIRST SESSION</span>;
  if (index >= 0 && names) {
    where = (
      <>
        <span className="font-mono text-[11px] tracking-[0.1em] text-[#777982]">SETUP {index + 1} OF {COUNTED.length}</span>
        <div className="leading-tight">
          <b className="block text-[14px] text-[#18191d]">{names[0]}</b>
          <small className="text-[12px] text-[#777982]">{names[1]}</small>
        </div>
      </>
    );
  }
  let width = 0;
  if (index >= 0) width = ((index + 1) / COUNTED.length) * 100;
  return (
    <div className="min-h-screen bg-[#f6f6f4]">
      <header className="border-b border-[#e6e6e3] bg-white">
        <div className="mx-auto flex max-w-[1080px] items-center gap-5 px-5 py-4">
          <img src="/logo-black.png" alt="Agents Anywhere" className="h-6 w-auto object-contain" />
          <div className="flex items-center gap-3 border-l border-[#e6e6e3] pl-5">{where}</div>
        </div>
        <div className="h-[3px] bg-[#ececea]"><div className="h-[3px] bg-[#18191d] transition-all" style={{ width: `${width}%` }} /></div>
      </header>
      <main className={`mx-auto px-5 py-10 ${wide ? 'max-w-[1080px]' : 'max-w-[860px]'}`}>{children}</main>
    </div>
  );
};

export const StepTitle: React.FC<{ eyebrow: string; title: string; lead?: React.ReactNode; aside?: React.ReactNode }> = ({ eyebrow, title, lead, aside }) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-5">
    <div className="max-w-[640px]">
      <div className="mb-2 font-mono text-[11.5px] font-semibold uppercase tracking-[0.1em] text-[#777982]">{eyebrow}</div>
      <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.015em] text-[#18191d]">{title}</h1>
      {lead && <p className="mt-2 text-[15px] leading-[1.6] text-[#666971]">{lead}</p>}
    </div>
    {aside}
  </div>
);

export const Footer: React.FC<{ left?: React.ReactNode; right: React.ReactNode }> = ({ left, right }) => (
  <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#ececea] pt-5">
    <div>{left}</div>
    <div className="flex flex-wrap items-center gap-2">{right}</div>
  </div>
);

export const DECISION_LABEL: Record<Decision, string> = { yes: 'Allow', approval: 'Ask first', no: 'Block' };
const DECISIONS: Decision[] = ['yes', 'approval', 'no'];

export const DecisionControl: React.FC<{
  value: Decision | 'mixed' | null;
  onChange: (d: Decision) => void;
  disabled?: boolean;
  small?: boolean;
}> = ({ value, onChange, disabled, small }) => (
  <div className="inline-flex overflow-hidden rounded-[7px] border border-[#dedede]" role="group">
    {DECISIONS.map((d, i) => {
      let tone = 'bg-white text-[#666971] hover:text-[#18191d]';
      if (value === d) tone = 'bg-[#18191d] text-white';
      return (
        <button
          key={d}
          type="button"
          disabled={disabled}
          onClick={() => onChange(d)}
          className={`${small ? 'px-2 py-1 text-[11.5px]' : 'px-3 py-1.5 text-[12.5px]'} font-semibold ${tone} ${i > 0 ? 'border-l border-[#dedede]' : ''} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {DECISION_LABEL[d]}
        </button>
      );
    })}
  </div>
);

export const Chip: React.FC<{ tone?: 'plain' | 'good' | 'warn' | 'violet' | 'ink'; children: React.ReactNode }> = ({ tone = 'plain', children }) => {
  const tones: Record<string, string> = {
    plain: 'bg-[#eef3ff] text-[#18191d]',
    good: 'bg-[#e3f3e9] text-[#1d7a45]',
    warn: 'bg-[#fff4cc] text-[#8a5a00]',
    violet: 'bg-[#ece7ff] text-[#5b3fc4]',
    ink: 'bg-[#18191d] text-white',
  };
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-[2px] font-mono text-[10.5px] font-medium ${tones[tone]}`}>{children}</span>;
};

export const KIND_LABEL: Record<IdeaKind, string> = { skill: 'Skill', plugin: 'Plugin', build: 'Build' };
export const FIT_LABEL: Record<IdeaFit, [string, 'good' | 'warn' | 'violet']> = {
  fits: ['Fits', 'good'],
  change: ['1 change', 'warn'],
  scoping: ['Scoping', 'violet'],
};

export const AppMark: React.FC<{ app: SetupApp; on?: boolean }> = ({ app, on }) => {
  const [broken, setBroken] = React.useState(false);
  const initials = app.label.replace(/[^A-Za-z0-9 ]/g, '').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  if (app.logo && !broken) {
    return (
      <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-[6px] ${on ? 'bg-white' : 'bg-[#f2f4f8]'}`}>
        <img src={app.logo} alt="" className="h-[18px] w-[18px] object-contain" onError={() => setBroken(true)} />
      </span>
    );
  }
  return (
    <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-[6px] font-mono text-[10px] font-semibold ${on ? 'bg-[#18191d] text-white' : 'bg-[#eef3ff] text-[#18191d]'}`}>
      {initials}
    </span>
  );
};

/*
 * The first thing a person sees, whether they signed up or came by a link:
 * one card with the logo and "First session" across its top, a short line,
 * and only the fields we do not already have.
 */
export const FIRST_INPUT = 'h-[48px] w-full rounded-[8px] border border-[#dedede] bg-white px-4 text-[15px] text-[#18191d] outline-none placeholder:text-[#92949d] focus:border-[#18191d]';
export const FIRST_LABEL = 'mb-2 block text-[14px] font-semibold text-[#18191d]';
export const FIRST_BUTTON = 'inline-flex h-[48px] items-center justify-center gap-2.5 rounded-[8px] bg-[#18191d] px-6 text-[15px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50';

export const FirstSessionCard: React.FC<{
  title: string;
  lead?: React.ReactNode;
  children: React.ReactNode;
  below?: React.ReactNode;
  /** A second column beside the form, on wide screens. */
  aside?: React.ReactNode;
}> = ({ title, lead, children, below, aside }) => {
  let width = 'max-w-[540px]';
  if (aside) width = 'max-w-[940px]';
  return (
    <div className="min-h-screen bg-[#f6f6f4] px-4 py-12 sm:py-16">
      <div className={`mx-auto ${width}`}>
        <div className="overflow-hidden rounded-[14px] border border-[#e1e4ea] bg-white">
          <div className="flex items-center justify-between gap-4 bg-[#eef3ff] px-6 py-5">
            <img src="/logo-black.png" alt="Agents Anywhere" className="h-6 w-auto object-contain" />
            <span className="font-mono text-[12px] font-medium tracking-[0.14em] text-[#4a4d55]">FIRST SESSION</span>
          </div>
          <div className={aside ? 'grid md:grid-cols-[1fr_320px]' : ''}>
            <div className="space-y-5 px-6 pb-7 pt-7 sm:px-8">
              <div>
                <h1 className="m-0 text-[24px] font-bold leading-tight text-[#18191d]">{title}</h1>
                {lead && <p className="m-0 mt-3 text-[15px] leading-[1.65] text-[#666971]">{lead}</p>}
              </div>
              {children}
            </div>
            {aside && <div className="border-t border-[#e1e4ea] bg-[#fafaf9] px-6 py-7 md:border-l md:border-t-0">{aside}</div>}
          </div>
        </div>
        {below}
      </div>
    </div>
  );
};
